// (c) 2026 Owl Consultations. All rights reserved.
// Proprietary and confidential — licensed use only. Unauthorized use,
// copying, or distribution is prohibited and may be prosecuted.

import { useState } from 'react'
import { ShieldAlert, ChevronRight } from 'lucide-react'
import Button from '../../components/ui/AppButton'
import type * as api from '../../api/client'
import { rowOpen, rowActions } from './rowOpen'
import { friendlyError } from '../../api/errors'
import { formatUsDate } from '../../api/format'
import CareRxClinicalReviewDetail from './CareRxClinicalReviewDetail'
import { analyteLabel } from '../../lib/analytes'
import { useTableFunnels, type FunnelColumn } from '../../components/common/TableFunnels'
import LanguageBadge from '../../components/ui/LanguageBadge'
import TherapyRefTag from '../../components/ui/TherapyRefTag'
import { useRefreshClinicalCounts, useSetHoldFollowUp } from '../../hooks/queries/carerxClinical'
import { resolutionText, useResolveHolds, useScreeningHolds, useScreeningRules } from '../../hooks/queries/screeningHolds'
import DateInput from '../../components/ui/DateInput'
import ConfirmDialog from '../../components/ui/ConfirmDialog'
import LoadState from '../../components/ui/LoadState'
import PageHeader from '../../components/ui/PageHeader'
import StatusChip from '../../components/ui/StatusChip'
import TruncationNotice from '../../components/ui/TruncationNotice'
import { useToast } from '../../components/Toast'
import { labelOf } from '../../ui/statusLabels'
import ManualHoldPanel from './ManualHoldPanel'

/** Today as YYYY-MM-DD in the user's own time zone — ISO dates compare as strings. */
const todayIso = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}` }

// D-CRX-45 Phase 4 (CR-F1/F2): Screening & Hold as its own tab. Lists RXs routed here from a
// Conditional determination (CR-D6) or a lab-result rule match (CR-V5). Resolving a hold returns the
// RX to Clinical Review. The seeded workbook rule set is shown as reference. Decision support only —
// every hold/resume needs pharmacist documentation + prescriber notification.
//
// D-UI-3 pilot (2026-10-04): the first screen on the shared kit — data through query hooks (E-02), a
// LoadState that never shows "no holds" before the list has loaded, typed toasts instead of a message
// whose colour was guessed from its text, status chips, and resolving through a confirm dialog.

const th: React.CSSProperties = { fontSize: 10.5, letterSpacing: '.03em', textTransform: 'uppercase', color: 'var(--color-text-soft, #57534e)', fontWeight: 700, textAlign: 'left', padding: '8px 10px', borderBottom: '1.5px solid #dde5ec', whiteSpace: 'nowrap' }
const td: React.CSSProperties = { padding: '9px 10px', borderBottom: '1px solid #eef2f6', fontSize: 12.5, verticalAlign: 'middle' }
const inp: React.CSSProperties = { fontSize: 12, padding: '4px 7px', border: '1px solid #dde5ec', borderRadius: 6, background: '#fff' }
const tagS = (bg: string, c: string): React.CSSProperties => ({ fontSize: 10.5, padding: '2px 9px', borderRadius: 100, fontWeight: 600, background: bg, color: c, whiteSpace: 'nowrap', display: 'inline-block' })

type Hold = api.ScreeningHoldRow['holds'][number]
/** Every hold source by name (audit C9: a screening-rule hold used to be labelled "manual"). */
const sourceLabel = (h: Hold) =>
  (h.source === 'lab_rule' || h.source === 'screening_rule') && h.rule_ref ? h.rule_ref : labelOf(h.source, 'hold_source')
const holdLine = (h: Hold) => [h.trigger_text, h.result_text].filter(Boolean).join(' · ') || h.hold_decision || '—'

// D-UI-2: a funnel on every column. "Why it is held" is the text of the hold lines the cell
// prints (open holds, or all of them when none is open), searched as free text.
const holdWhyText = (r: api.ScreeningHoldRow): string => {
  const openHolds = r.holds.filter(h => h.status !== 'resolved')
  return (openHolds.length ? openHolds : r.holds).map(h => [
    sourceLabel(h),
    holdLine(h),
    h.hold_decision && h.trigger_text ? `— ${h.hold_decision}` : '',
    h.status === 'resolved' ? '· resolved' : '',
  ].filter(Boolean).join(' ')).join('\n')
}
const HOLD_FUNNELS: FunnelColumn<api.ScreeningHoldRow>[] = [
  { key: 'rx', label: 'RX #', value: r => r.rx_number || '—' },
  { key: 'patient', label: 'Patient', value: r => r.patient_name || '—' },
  { key: 'drug', label: 'Drug', value: r => r.drug_name || '—' },
  { key: 'why', label: 'Why it is held', value: holdWhyText, kind: 'text' },
  { key: 'held_since', label: 'Held since', value: r => r.held_since },
  // ASP #22: when the pharmacist will next look at it.
  { key: 'follow_up', label: 'Follow up', value: r => r.follow_up_on || '—' },
  { key: 'status', label: 'Status', value: r => labelOf(r.status) },
]
const RULE_FUNNELS: FunnelColumn<api.ScreeningRule>[] = [
  { key: 'ref', label: 'Ref', value: r => r.rule_ref },
  { key: 'condition', label: 'Condition', value: r => r.condition },
  { key: 'analyte', label: 'Analyte', value: r => (r.analyte ? analyteLabel(r.analyte) : '—') },
  { key: 'trigger', label: 'Trigger', value: r => r.trigger || '—', kind: 'text' },
  { key: 'hold_decision', label: 'Hold decision', value: r => r.hold_decision || '—', kind: 'text' },
  { key: 'scope', label: 'Scope', value: r => r.scope || '—' },
]

export default function CareRxScreeningHoldV2() {
  const holdsQ = useScreeningHolds()
  const rows = holdsQ.data?.items ?? []
  const truncated = !!holdsQ.data?.truncated
  const cap = holdsQ.data?.cap ?? null
  const toast = useToast()
  const resolve = useResolveHolds()
  const followUp = useSetHoldFollowUp()
  // An RX on hold still has to be READABLE — labs, results, consultation, the whole record.
  // Same detail view Review uses, so there is one enrollment record, reachable from any stage.
  const [openTherapy, setOpenTherapy] = useState<string | null>(null)
  // The therapy being resolved, by id: its holds are read from the CURRENT list (a refetch after a partial
  // failure, or a colleague's resolve, changes them) — never from a snapshot taken when the dialog opened.
  const [resolveId, setResolveId] = useState<string | null>(null)
  const resolveRow = resolveId ? rows.find(r => r.therapy_id === resolveId) ?? null : null
  const [notified, setNotified] = useState(false)
  const [resolveErr, setResolveErr] = useState<string | null>(null)
  const [manualOpen, setManualOpen] = useState(false)
  const [showRules, setShowRules] = useState(false)
  const rulesQ = useScreeningRules(showRules)
  const rules = rulesQ.data?.rules ?? []
  const holdFx = useTableFunnels(rows, HOLD_FUNNELS)
  const ruleFx = useTableFunnels(rules, RULE_FUNNELS)
  const refreshCounts = useRefreshClinicalCounts()
  const reload = () => { void holdsQ.refetch(); refreshCounts() }

  const startResolve = (row: api.ScreeningHoldRow) => { setResolveId(row.therapy_id); setNotified(false); setResolveErr(null) }
  // The row is an ENROLLMENT, so resolving it clears every open hold on it — a therapy is only off hold
  // when nothing is still holding it, and that is also what returns it to Review. One audited request per
  // hold, in order; a failure stops there and says how far it got (useResolveHolds).
  const doResolve = (note: string) => {
    const row = resolveRow
    if (!row) return
    const rx = row.rx_number ? `Rx ${row.rx_number}` : 'This Rx'
    resolve.mutate({ row, resolution: resolutionText(note, notified), prescriberNotified: notified }, {
      onSuccess: o => {
        if (o.error) {
          setResolveErr(`${o.resolved} of ${o.total} holds resolved, then: ${friendlyError(o.error)} The rest are still on hold.`)
          toast.show(`${rx}: ${o.resolved} of ${o.total} holds resolved — the rest are still on hold.`, 'error')
          return
        }
        setResolveId(null)
        toast.show(o.total > 1 ? `Resolved ${o.total} holds — ${rx} returned to Review.` : `Resolved — ${rx} returned to Review.`, 'success')
      },
      onError: e => setResolveErr(friendlyError(e)),
    })
  }

  // Every count is per RX now, not per hold event: three positive analytes on one enrollment are
  // one RX on hold, not three.
  const open = rows.filter(r => r.open_holds > 0)
  const positive = rows.filter(r => r.open_holds > 0
    && r.holds.some(h => h.status !== 'resolved' && (h.result_text ?? '').toLowerCase().includes('positive')))
  const resolved = rows.filter(r => r.open_holds === 0)

  if (openTherapy) {
    // D-CRX-73 — the rule panel is normally OFF here: Hold runs its own lab-driven rules and a
    // second question set is noise. A therapy the RULES held is the exception: without the panel it
    // lands where nobody can answer the questions that put it there. Shown for rule-sourced holds only.
    const heldByRule = rows.some(r => r.therapy_id === openTherapy
      && r.holds.some(h => h.status !== 'resolved' && h.source === 'screening_rule'))
    return <CareRxClinicalReviewDetail therapyId={openTherapy} backLabel="Back to Hold" showScreening={heldByRule}
      alreadyOnHold onBack={() => { setOpenTherapy(null); reload() }} />
  }

  return (
    <div>
      <PageHeader icon={<ShieldAlert size={18} color="#93610f" />} title="Screening & Hold"
        subtitle="Conditional determinations and rule matches route here"
        // ASP #24: the manual trigger moved here from Renewals — an event puts the therapy on hold.
        actions={<Button variant="outlined" color="warning" onClick={() => setManualOpen(v => !v)}>{manualOpen ? 'Close' : 'Manual hold'}</Button>} />
      {manualOpen && <ManualHoldPanel onCancel={() => setManualOpen(false)} onPlaced={m => { setManualOpen(false); toast.show(m, 'success'); reload() }} />}

      <div style={{ display: 'flex', gap: 9, flexWrap: 'wrap', marginBottom: 14 }}>
        {([['On hold', open.length, '#93610f'], ['Positive screen', positive.length, '#b23a48'], ['Resolved', resolved.length, '#0d6b52']] as const).map(([l, v, c]) => (
          <div key={l} style={{ flex: '1 1 120px', minWidth: 112, background: '#fff', border: '1px solid #dde5ec', borderRadius: 10, padding: '10px 13px' }}>
            {/* A count is a statement — only a loaded list may make it. */}
            <div style={{ fontSize: 21, fontWeight: 700, color: c }}>{holdsQ.data !== undefined ? v : '—'}</div>
            <div style={{ fontSize: 11, color: '#41566a' }}>{l}</div>
          </div>
        ))}
      </div>

      <div style={{ fontSize: 11.5, color: '#41566a', background: '#eaf2fb', borderRadius: 8, padding: '8px 11px', marginBottom: 12 }}>
        A <b>Conditional</b> determination or a positive lab result matching a rule routes the RX here. Resolve the hold to return it to Clinical Review. <b>Decision support only</b> — every hold/resume needs pharmacist documentation + prescriber notification.
      </div>

      {truncated && <TruncationNotice shown={cap ?? rows.length} what="holds" detail="full pagination is coming" />}

      {/* "No RXs on hold." only after a successful load; a failed reload of an empty list shows the warning only. */}
      <LoadState query={holdsQ} what="the holds" isEmpty={d => (d.items ?? []).length === 0} empty="No RXs on hold.">{() => (
        <>{holdFx.chips}
        <div style={{ overflowX: 'auto', border: '1px solid #dde5ec', borderRadius: 10 }}>
          <table {...holdFx.tableProps} style={{ borderCollapse: 'collapse', width: '100%', minWidth: 960 }}>
            <thead><tr>
              <th style={th}>RX # {holdFx.funnel('rx')}</th><th style={th}>Patient {holdFx.funnel('patient')}</th><th style={th}>Drug {holdFx.funnel('drug')}</th>
              <th style={th}>Why it is held {holdFx.funnel('why')}</th><th style={th}>Held since {holdFx.funnel('held_since')}</th><th style={th}>Follow up {holdFx.funnel('follow_up')}</th><th style={th}>Status {holdFx.funnel('status')}</th><th style={th}></th>
            </tr></thead>
            <tbody>
              {holdFx.rows.map(r => {
                const openHolds = r.holds.filter(h => h.status !== 'resolved')
                const shown = openHolds.length ? openHolds : r.holds
                return (
                <tr key={r.therapy_id} {...rowOpen(() => setOpenTherapy(r.therapy_id))}>
                  <td style={{ ...td, fontFamily: 'ui-monospace', fontWeight: 700, verticalAlign: 'top' }}>{r.rx_number || '—'}</td>
                  <td style={{ ...td, verticalAlign: 'top' }}>{r.patient_name || '—'}<LanguageBadge language={r.preferred_language} /><div><TherapyRefTag therapyId={r.therapy_id} /></div></td>
                  <td style={{ ...td, verticalAlign: 'top' }}>{r.drug_name || '—'}</td>
                  {/* EVERY trigger on this enrollment, one line each — a lab that fires is a reason,
                      not another RX to work. */}
                  <td style={{ ...td, maxWidth: 430 }}>
                    {shown.map(h => (
                      <div key={h.id} style={{ display: 'flex', gap: 6, alignItems: 'baseline', marginBottom: 3, opacity: h.status === 'resolved' ? .55 : 1 }}>
                        <span style={tagS(h.source === 'manual' ? '#eef1f4' : '#eaf2fb', h.source === 'manual' ? '#475569' : '#185FA5')}>
                          {sourceLabel(h)}
                        </span>
                        <span style={{ fontSize: 11.5 }}>
                          {holdLine(h)}
                          {h.hold_decision && h.trigger_text ? <span style={{ color: 'var(--color-text-soft, #57534e)' }}> — {h.hold_decision}</span> : null}
                          {h.status === 'resolved' ? <span style={{ color: '#0d6b52' }}> · resolved</span> : null}
                        </span>
                      </div>
                    ))}
                    {r.hold_count > shown.length && (
                      <div style={{ fontSize: 10.5, color: 'var(--color-text-soft, #57534e)' }}>+{r.hold_count - shown.length} resolved earlier</div>
                    )}
                  </td>
                  <td style={{ ...td, whiteSpace: 'nowrap', verticalAlign: 'top' }}>{r.held_since ? formatUsDate(r.held_since) : '—'}</td>
                  {/* ASP #22: set on the therapy's open holds; past due shows in the warning colour. */}
                  <td style={{ ...td, whiteSpace: 'nowrap', verticalAlign: 'top' }} {...rowActions}>
                    {r.status === 'resolved' ? '—' : (
                      <DateInput label={`Follow-up date for Rx ${r.rx_number ?? ''}`} value={r.follow_up_on}
                        emphasis={r.follow_up_on && r.follow_up_on < todayIso() ? 'overdue' : undefined}
                        title={r.follow_up_on && r.follow_up_on < todayIso() ? 'Follow-up date has passed' : 'When to look at this hold again'}
                        disabled={followUp.isPending && followUp.variables?.therapyId === r.therapy_id}
                        // Review L12: per-call promise — mutate()'s callbacks fire only for the LATEST
                        // call. Returned, so a refused date also reverts in the field (DateInput, S8).
                        onChange={d => followUp.mutateAsync({ therapyId: r.therapy_id, followUpOn: d }).then(
                          () => { toast.show(d ? 'Follow-up date saved.' : 'Follow-up date cleared.', 'success'); reload() },
                          (e: unknown) => { toast.show(friendlyError(e), 'error'); throw e })} />
                    )}
                  </td>
                  <td style={{ ...td, verticalAlign: 'top' }}>
                    <StatusChip status={r.status} tone={r.status === 'on_hold' ? 'amber' : undefined} />
                    {r.open_holds > 1 && <div style={{ fontSize: 10.5, color: '#93610f' }}>{r.open_holds} open holds</div>}
                  </td>
                  <td style={{ ...td, verticalAlign: 'top', whiteSpace: 'nowrap' }} {...rowActions}>
                    <button onClick={() => setOpenTherapy(r.therapy_id)} title="Open the enrollment record"
                      style={{ ...inp, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 2, marginRight: 6 }}>
                      Open <ChevronRight size={13} />
                    </button>
                    {r.open_holds > 0 && <Button variant="contained" onClick={() => startResolve(r)}>Resolve…</Button>}
                  </td>
                </tr>
              )})}
              {holdFx.rows.length === 0 && <tr><td colSpan={8} style={{ ...td, textAlign: 'center', color: 'var(--color-text-soft, #57534e)', padding: 24 }}>{rows.length === 0 ? 'No RXs on hold.' : 'No rows match the current filters.'}</td></tr>}
            </tbody>
          </table>
        </div></>
      )}</LoadState>

      {/* D-UI-3 pilot (audit C10): one dialog that names what is cleared, requires the note and the
          prescriber notification the banner above demands, and cannot be sent twice. */}
      <ConfirmDialog open={!!resolveRow} busy={resolve.isPending} error={resolveErr}
        title={resolveRow && resolveRow.open_holds > 1 ? `Resolve ${resolveRow.open_holds} holds` : 'Resolve hold'}
        confirmLabel={resolveRow && resolveRow.open_holds > 1 ? `Resolve all ${resolveRow.open_holds}` : 'Resolve'}
        requireReason={{ label: 'Resolution note', minLength: 3, placeholder: 'What was decided, and why' }}
        // Required: the banner above says every resume needs prescriber notification. resolutionText still
        // records the answer explicitly so the stored note reads on its own.
        confirmDisabled={!notified}
        onCancel={() => setResolveId(null)} onConfirm={({ reason }) => doResolve(reason)}
        body={resolveRow && <>
          <div style={{ marginBottom: 8 }}>Rx <b>{resolveRow.rx_number || '—'}</b> · {resolveRow.patient_name || '—'}<LanguageBadge language={resolveRow.preferred_language} /> · {resolveRow.drug_name || '—'}. Resolving returns it to Clinical Review.</div>
          <ul style={{ margin: '0 0 10px', paddingLeft: 18, fontSize: 12.5 }}>
            {resolveRow.holds.filter(h => h.status !== 'resolved').map(h => (
              <li key={h.id}><b>{sourceLabel(h)}</b> — {holdLine(h)}</li>
            ))}
          </ul>
          <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 13, cursor: 'pointer' }}>
            <input type="checkbox" checked={notified} onChange={e => setNotified(e.target.checked)} disabled={resolve.isPending} />
            The prescriber has been notified
          </label>
        </>} />

      {/* Rule reference */}
      <div style={{ marginTop: 16 }}>
        <button onClick={() => setShowRules(s => !s)} style={{ fontSize: 12.5, fontWeight: 600, color: '#185FA5', cursor: 'pointer', border: 'none', background: 'none', padding: 0 }}>
          {showRules ? '▾' : '▸'} Screening &amp; Hold rule set{rulesQ.data ? ` (${rules.length} triggers, from the protocol workbook)` : ''}
        </button>
        {showRules && (
          <LoadState query={rulesQ} what="the rule set" isEmpty={d => (d.rules ?? []).length === 0} empty="No screening rules are published.">{() => (
          <>{ruleFx.chips}
          <div style={{ overflowX: 'auto', border: '1px solid #dde5ec', borderRadius: 10, marginTop: 8 }}>
            <table {...ruleFx.tableProps} style={{ borderCollapse: 'collapse', width: '100%', minWidth: 820 }}>
              <thead><tr><th style={th}>Ref {ruleFx.funnel('ref')}</th><th style={th}>Condition {ruleFx.funnel('condition')}</th><th style={th}>Analyte {ruleFx.funnel('analyte')}</th><th style={th}>Trigger {ruleFx.funnel('trigger')}</th><th style={th}>Hold decision {ruleFx.funnel('hold_decision')}</th><th style={th}>Scope {ruleFx.funnel('scope')}</th></tr></thead>
              <tbody>
                {ruleFx.rows.map(r => (
                  <tr key={r.rule_ref}>
                    <td style={{ ...td, fontWeight: 700 }}>{r.rule_ref}</td>
                    <td style={td}>{r.condition}</td>
                    <td style={{ ...td, color: 'var(--color-text-soft, #57534e)' }}>{r.analyte ? analyteLabel(r.analyte) : '—'}</td>
                    <td style={{ ...td, fontSize: 11.5 }}>{r.trigger || '—'}</td>
                    <td style={{ ...td, fontSize: 11.5 }}>{r.hold_decision || '—'}</td>
                    <td style={{ ...td, fontSize: 11.5, color: 'var(--color-text-soft, #57534e)' }}>{r.scope || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div></>
          )}</LoadState>
        )}
      </div>
    </div>
  )
}
